import type { FC } from 'react';
import { StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { PrimaryButton, SecondaryButton } from 'src/presentation/components/molecules/Button';
import {
	ReservationDate,
	ReservationPersons,
	ReservationTime,
} from 'src/presentation/components/molecules/ReservationAttributes';
import WorkspaceRow from 'src/presentation/components/molecules/WorkspaceRow';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import type { ApiReservationId } from 'src/core/models/api/ApiReservation';
import type { AccountNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Translations from 'src/core/utils/Translations';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';

interface Props {
	id: ApiReservationId;
	label: string | number;
	personCount: number;
	arrivalTime: string;
	arrivalDate: string;
	workspaceTitle: string;
	extraStyle?: ViewStyle;
	last?: boolean;
	pastOrders?: boolean;
	image?: string;
	reservationFlowUrl?: string | null;
	canRebook?: boolean | null;
}

const ReservationCard: FC<Props> = (props) => {
	const lastStyle = !props.last ? styles.borderBottom : {};
	const style = { ...styles.card, ...lastStyle, ...props.extraStyle };
	const navigation = useNavigation<StackNavigationProp<AccountNavigation>>();
	const { confirmLeaving } = useLeaveAppStore();

	const startReservation = (url: string) =>
		confirmLeaving({
			url,
			description: Translations.reserveLeaveAppDescription,
			webMessage: Constants.bridgeMessageReservationFlowOpen,
		});

	return (
		<View style={[style, { borderRadius: Dimens.borderRadius10 }]}>
			<Text style={styles.sansText}>{props.label || Translations.notAvailableShort}</Text>
			<View style={[styles.rowDirection, { justifyContent: 'space-between', paddingBottom: Dimens.padding10 }]}>
				<ReservationDate arrivalDate={props.arrivalDate} />
				<ReservationTime arrivalTime={props.arrivalTime} />
				<ReservationPersons personsCount={props.personCount} />
			</View>
			<WorkspaceRow image={props.image ?? null} title={props.workspaceTitle} />
			{props.pastOrders ? (
				<View style={[styles.rowDirection, { width: '100%', gap: Dimens.gap15 }]}>
					<SecondaryButton
						extraStyle={styles.detailsButton}
						label={Translations.view}
						onPress={() =>
							navigation.navigate(Constants.screenReservationDetail, {
								value: props.id,
								pastOrder: props.pastOrders,
							})
						}
					/>
					<PrimaryButton
						disabled={props.canRebook === false || !props.reservationFlowUrl}
						extraStyle={styles.bookAgainButton}
						label={Translations.reservationBookAgain}
						onPress={() => (props.reservationFlowUrl ? startReservation(props.reservationFlowUrl) : undefined)}
					/>
				</View>
			) : (
				<PrimaryButton
					label={Translations.reservationViewReservation}
					onPress={() =>
						navigation.navigate(Constants.screenReservationDetail, {
							value: props.id,
						})
					}
				/>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	detailsButton: {
		minWidth: Dimens.size130,
		flex: Dimens.flex0,
	},
	bookAgainButton: {
		flex: Dimens.flex1,
	},
	card: {
		flexDirection: 'column',
		marginBottom: Dimens.padding10,
		backgroundColor: Colors.white,
		padding: Dimens.padding15,
	},
	rowDirection: {
		flexDirection: 'row',
	},
	borderBottom: {
		borderColor: Colors.gray200,
		borderBottomWidth: Dimens.borderWidth1,
	},
	sansText: {
		fontSize: Dimens.fontSize19,
		paddingBottom: Dimens.padding15,
		fontFamily: Fonts.ptSerif,
	},
});

export default ReservationCard;
